import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

export default function Login() {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const adminMode = params.get("admin") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || "Login failed");
      toast.error(res.error || "Login failed");
      return;
    }
    // Admin portal mode: only admin accounts allowed through.
    if (adminMode && res.user.role !== "admin") {
      await logout();
      setError("This account is not an SCU administrator. Use the Parent Portal to sign in.");
      toast.error("Admin sign-in required. Use Parent Portal instead.");
      return;
    }
    toast.success(`Welcome back, ${res.user.name}!`);
    nav(res.user.role === "admin" ? "/admin" : "/portal");
  };

  return (
    <div data-testid="login-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-white border border-scu-smoke/15 rounded-md p-8">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} className="h-12" alt="SCU" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold flex items-center gap-1.5">
              {adminMode && <ShieldCheck className="h-3.5 w-3.5" />}
              {adminMode ? "Admin Portal" : "Parent Portal"}
            </div>
            <div className="font-bebas text-3xl tracking-wide text-scu-obsidian leading-none">Sign In</div>
          </div>
        </div>
        {adminMode && (
          <div data-testid="admin-mode-banner" className="mt-4 text-xs bg-scu-purple/5 border border-scu-purple/30 text-scu-purple rounded-md p-3">
            Administrator sign-in — only SCU admin accounts are accepted here.
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">Email</Label>
            <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">Password</Label>
              <Link data-testid="login-forgot-link" to="/forgot-password" className="text-xs text-scu-purple hover:text-scu-electric font-bold">Forgot password?</Link>
            </div>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-10 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-ring md:text-sm"
              />
              <button data-testid="login-password-toggle" type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-scu-smoke hover:text-scu-purple" aria-label="Toggle password visibility">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <div data-testid="login-error" className="text-sm text-red-600">{error}</div>}
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
            {loading ? "Signing in…" : (adminMode ? "Admin Sign In" : "Sign In")}
          </Button>
        </form>
        {!adminMode && (
          <div className="mt-6 text-sm text-center text-scu-smoke">
            No account? <Link to="/signup" data-testid="login-signup-link" className="text-scu-purple hover:text-scu-electric font-bold">Create one</Link>
          </div>
        )}
      </div>
    </div>
  );
}
